import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { MessageSquareText } from "lucide-react";

/**
 * The dense little state pills the Nawy Now surfaces use (Ready, the step outcome, Escalated).
 *
 * Lifted out of `step-card.tsx` when §Escalate-report needed the Escalated pill on the dashboard queue and the
 * leads kanban too. Sharing it rather than re-declaring the red is not fussiness: this feature already produced
 * three copies of the escalate confirmation modal that had to be folded back into one during review, and a pill
 * whose colour drifts between the queue and the board reads as two different states rather than one.
 *
 * Deliberately not a `<Chip>`: these rows are dense, every neighbouring pill is a 10px/16px box, and a
 * default-height MUI Chip would be the tallest thing in the row — making escalated rows taller than the rest.
 */
export const PILL = {
  blue: { bg: "#CBE3F4", fg: "#013355" },
  green: { bg: "#CFE8E2", fg: "#326054" },
  red: { bg: "#FFC8C2", fg: "#AB2018" },
  yellow: { bg: "#F3F3C4", fg: "#5D5D13" },
  grey: { bg: "#E0E0E0", fg: "#313131" },
} as const;

export const StatusPill = ({
  tone,
  label,
  radius,
  title,
  labelClassName,
  trailing,
}: {
  tone: keyof typeof PILL;
  label: string;
  radius: string;
  title?: string;
  /** Hook for the host to style the label text alone (the hover underline on the interactive pill). */
  labelClassName?: string;
  /**
   * An optional glyph after the label. Its only current use is marking a pill that DOES something —
   * see EscalatedPill — so it deliberately sits inside the pill's own ground rather than beside it:
   * an icon floating next to the chip reads as a second, separate control.
   */
  trailing?: React.ReactNode;
}) => (
  <Box
    title={title}
    sx={{
      alignSelf: "center",
      display: "inline-flex",
      alignItems: "center",
      gap: trailing ? "3px" : 0,
      px: "4px",
      py: 0,
      borderRadius: radius,
      backgroundColor: PILL[tone].bg,
      flexShrink: 0,
    }}
  >
    <Typography
      className={labelClassName}
      sx={{
        fontSize: 10,
        lineHeight: "16px",
        fontWeight: 400,
        textAlign: "center",
        color: PILL[tone].fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    {trailing}
  </Box>
);

/**
 * §Escalate-report: "this step has been escalated", identical on every surface that lists Nawy Now work.
 *
 * Inert by default — the escalation is a state of the step, and the row's own click target still owns the row.
 * That is still true of the dashboard queue and the step card, which pass no handler and are unchanged.
 *
 * §Escalate-thread-board: the board card is the exception, and it is an exception on purpose. Its rows are the
 * one surface with nowhere else to go: a supervisor reading the board cannot open the step (it is somebody
 * else's), so the pill is the only thing on screen that knows an escalation exists. Given `onClick` it becomes
 * a real button — keyboard reachable, cursor and hover included — rather than a Box with a listener bolted on,
 * because a pill that silently behaves differently on one surface is worse than one that looks different.
 */
export const EscalatedPill = ({ onClick }: { onClick?: () => void } = {}) => {
  const theme = useTheme();

  if (!onClick) {
    return (
      <StatusPill
        tone="red"
        radius={theme.nawy.radius.md}
        label="Escalated"
        title="This step has been escalated"
      />
    );
  }

  /**
   * The interactive pill has to LOOK different from the inert one, not merely behave differently.
   *
   * The first cut of this kept the two visually identical — same chip, same colour, nothing but a cursor
   * and a tooltip to tell them apart — on the reasoning that the board should not sprout a new control.
   * That was wrong in the only way that matters: a status chip is the last thing anyone thinks to click,
   * so the thread was shipped and nobody could find it. A tooltip does not help, because it appears only
   * once you have already hovered the thing you had no reason to hover.
   *
   * Three cheap signals, no new row on the card: the speech bubble says "there is a conversation here",
   * the outline says "this is a control and not a label", and the underline on hover confirms it before
   * the click. The word stays "Escalated" so the pill still reports the state it always reported.
   */
  return (
    <ButtonBase
      onClick={(event) => {
        // Every host of this pill is itself clickable — the board card opens the lead — so a bubbled click
        // would open the thread AND navigate out from under it. Kept here so a new host cannot forget.
        event.stopPropagation();
        onClick();
      }}
      aria-label="Open the escalation thread"
      sx={{
        borderRadius: theme.nawy.radius.md,
        border: `1px solid ${PILL.red.fg}59`,
        cursor: "pointer",
        transition: "filter 120ms ease-out",
        "&:hover": {
          filter: "brightness(0.96)",
          "& .escalated-pill-label": { textDecoration: "underline" },
        },
        "&:focus-visible": { outline: `2px solid ${PILL.red.fg}`, outlineOffset: 1 },
      }}
    >
      <StatusPill
        tone="red"
        radius={theme.nawy.radius.md}
        label="Escalated"
        title="Open the escalation thread"
        labelClassName="escalated-pill-label"
        trailing={
          <MessageSquareText size={11} color={PILL.red.fg} style={{ display: "block" }} />
        }
      />
    </ButtonBase>
  );
};
